/**
 * @fileoverview Loader component for loading indicator
 * @module screen-view-v2/components/shared/Loader
 *
 * Renders a loading spinner. V2 uses LoaderConfig (kind + appearance + layout)
 * instead of V1's Loader type.
 */

import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { LoaderConfig } from '@segmently/schema-models/v2';
import { mapFrameLayoutStyles } from '../../../utils/styleMapper';
import styles from './Loader.module.css';

export interface LoaderProps {
  loader: LoaderConfig | undefined;
  className?: string;
  'data-testid'?: string;
}

export const Loader = memo(function Loader({
  loader,
  className,
  'data-testid': testId,
}: LoaderProps): React.ReactElement | null {
  if (!loader) {
    return null;
  }

  // V2: layout replaces box, appearance replaces styles
  const layoutStyles: CSSProperties = mapFrameLayoutStyles(loader.layout);
  const color = loader.appearance?.color;

  // Spinner style — V2 has no size in LoaderStyle, SCSS provides default
  const spinnerStyle: CSSProperties = {
    ...(color && { borderColor: `${color}33`, borderTopColor: color }),
  };

  const keyframes = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;

  return (
    <div
      className={className ? `${styles.loader} ${className}` : styles.loader}
      style={layoutStyles}
      data-testid={testId}
      role="status"
      aria-label="Loading"
    >
      <style>{keyframes}</style>
      <div
        className={styles.spinner}
        style={spinnerStyle}
        data-testid={testId ? `${testId}-spinner` : undefined}
      />
    </div>
  );
});

Loader.displayName = 'Loader';
